import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BRAND, CATALOG_PDFS } from "@/data/media";
import { Menu, X, Download } from "lucide-react";

const links = [
  { to: "/", key: "home" },
  { to: "/chi-siamo", key: "about" },
  { to: "/settori", key: "sectors" },
  { to: "/produzione", key: "production" },
  { key: "catalog" },
  { to: "/contatti", key: "contact" },
];

export const Navbar = () => {
  const { t, lang } = useLang();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const downloadCatalog = () => {
    window.open(CATALOG_PDFS[lang] || CATALOG_PDFS.it, "_blank");
  };

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        transparent
          ? "bg-transparent py-5"
          : "bg-cream/85 backdrop-blur-xl border-b border-brass/15 py-3 shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img src={BRAND.logoFull} alt="Sofas Contract" className="h-12 md:h-14 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l) =>
            l.key === "catalog" ? (
              <button
                key={l.key}
                onClick={downloadCatalog}
                data-testid="nav-link-catalog"
                className={`inline-flex items-center gap-1.5 text-[0.82rem] font-medium tracking-wide transition-colors ${transparent ? "text-white/90 hover:text-white" : "text-ink hover:text-brass"}`}
              >
                <Download className="w-3.5 h-3.5 text-brass" />
                {t.nav.catalog}
              </button>
            ) : (
            <NavLink
              key={l.key}
              to={l.to}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `relative text-[0.82rem] font-medium tracking-wide transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-brass after:transition-all after:duration-300 ${
                  isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                } ${transparent ? "text-white/90 hover:text-white" : "text-ink hover:text-brass"} ${
                  isActive ? (transparent ? "text-white" : "text-brass") : ""
                }`
              }
            >
              {t.nav[l.key]}
            </NavLink>
            )
          )}
          <div className="w-px h-5 bg-current opacity-20" />
          <LanguageSwitcher dark={transparent} />
        </nav>

        <div className="flex items-center gap-4 lg:hidden">
          <LanguageSwitcher dark={transparent} />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className={transparent ? "text-white" : "text-ink"}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-all duration-500 bg-cream ${
          open ? "max-h-[70vh] border-t border-brass/15" : "max-h-0"
        }`}
      >
        <nav className="flex flex-col px-6 py-4">
          {links.map((l) =>
            l.key === "catalog" ? (
              <button
                key={l.key}
                onClick={downloadCatalog}
                data-testid="mobile-nav-link-catalog"
                className="py-3 border-b border-brass/10 font-serif-display text-2xl text-ink flex items-center gap-2 text-left"
              >
                <Download className="w-5 h-5 text-brass" />
                {t.nav.catalog}
              </button>
            ) : (
            <NavLink
              key={l.key}
              to={l.to}
              data-testid={`mobile-nav-link-${l.key}`}
              className={({ isActive }) =>
                `py-3 border-b border-brass/10 font-serif-display text-2xl ${isActive ? "text-brass" : "text-ink"}`
              }
            >
              {t.nav[l.key]}
            </NavLink>
            )
          )}
        </nav>
      </div>
    </header>
  );
};
